import type {CertificateSurface} from "utils/gtm";
import type {Plan} from "types";
import {draftStorage, readExpiring, writeExpiring} from 'persistence';

const KEY = 'IA__post_login_action';
// An action is armed by the login it was handed to (useAuth's login) and consumed when that
// login completes, so it is only ever seconds — minutes at most, the OAuth round-trip being
// the long case — old. Expiring it covers the login that never came back (an OAuth redirect
// abandoned on the provider's page): its action must not fire on an unrelated login days
// later. The guest checkout session next door expires the same way, through the same helpers.
const TTL_MS = 2 * 60 * 60 * 1000; // 2h

export type PostLoginAction =
  | { type: 'free_premium_enroll' }
  // The certificate download an anon asked for before the login it needs: resumed by
  // uuid because an OAuth login reloads the page and the result panel comes back empty.
  | { type: 'download_certificate'; uuid: string; surface?: CertificateSurface }
  // An anon's simple result asked for its Deep Scan: run once the result is back on screen.
  | { type: 'deep_scan' }
  // The account-only page an anon was walled out of (AuthGuard): an OAuth login comes
  // back through the callback page and has to be sent on to it.
  | { type: 'account_page'; path: string }
  // A plan's checkout an anon was sent to (?upgrade=): a plan is bought on an account.
  | { type: 'upgrade'; plan: Plan };

/**
 * Set by the login itself, with the action the prompt that raised it handed over — `null`
 * for a voluntary one. That is what keeps it this login's: an action whose prompt the user
 * closed is cleared by the next login rather than replayed on top of it.
 */
export function setPostLoginAction(action: PostLoginAction | null) {
  if (action) writeExpiring<PostLoginAction>(KEY, action, draftStorage());
  else clearPostLoginAction();
}

export function consumePostLoginAction(): PostLoginAction | null {
  const action = readExpiring<PostLoginAction>(KEY, TTL_MS, draftStorage());
  clearPostLoginAction();
  return action?.type ? action : null;
}

function clearPostLoginAction() {
  try { draftStorage().removeItem(KEY); } catch {}
}

/**
 * The same thing for an action that cannot be serialized — today the files an anon
 * dropped, which are live File handles no localStorage entry can hold. It therefore
 * only survives a login that happens in place (the email/OTP form), which is as far as
 * a File can travel: an OAuth login reloads the page and takes the drop with it.
 * Armed by the login under the same rule as the persisted action above.
 */
let pendingCallback: (() => void) | null = null;

export function setPostLoginCallback(action: (() => void) | null) {
  pendingCallback = action;
}

export function consumePostLoginCallback(): (() => void) | null {
  const action = pendingCallback;
  pendingCallback = null;
  return action;
}
